import * as pc from 'playcanvas';
import { box, colorMaterial } from './app';
import { spawnCharacter } from './character';
import { spawnFacadeBuilding, TYPE_POOLS, FACADE_SETS } from './facade';
import { spawnProp } from './props';
import { spawnSplatBuilding } from './splat';
import { loadPbrTextures, pbrMaterial, pbrMaterialUV, type PbrTextureUrls } from './materials';
import type { Building, HouseSlot, Neighbor, WorldMap, XpProfile } from '../api/types';

/** Axis-aligned obstacle used for simple player collision. */
export interface Obstacle {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  /** Top of the obstacle, when it is low enough for the camera to see over
   * (plot walls). Omitted = treat as full-height (buildings). */
  maxY?: number;
}

export interface InteractTarget {
  building: Building;
  position: pc.Vec3;
  radius: number;
}

export interface WorldScene {
  obstacles: Obstacle[];
  interactables: InteractTarget[];
  /** Rebuilds the player's house after a profile update (the "magic loop"). */
  rebuildMyHouse(profile: XpProfile): void;
  districtAt(x: number, z: number): { id: string; nameAr: string; nameEn: string } | null;
}

const ROAD_COLOR = '#3a3f47';
const SIDEWALK_COLOR = '#8d939c';
const GROUND_COLOR = '#6fae5f';
/** Slight cool tint over the concrete shell of locked buildings ("closed doors") —
 * the bare windowless concrete already reads "closed"; heavy darkening just
 * renders as a dead black slab whenever the building falls in another's shadow. */
const LOCKED_TINT = '#b0b5bd';

// CC0 photo-PBR texture sets (Poly Haven + ambientCG — public/textures/LICENSE.txt).
// Everything visible is photo-sourced: ground, plazas, roads, sidewalks, facades.
const GRASS_TEXTURES: PbrTextureUrls = {
  diffuse: '/textures/grass/diffuse.jpg',
  normal: '/textures/grass/normal.jpg',
  ao: '/textures/grass/ao.jpg',
  rough: '/textures/grass/rough.jpg',
};
const CONCRETE_TEXTURES: PbrTextureUrls = {
  diffuse: '/textures/concrete/diffuse.jpg',
  normal: '/textures/concrete/normal.jpg',
  ao: '/textures/concrete/ao.jpg',
  rough: '/textures/concrete/rough.jpg',
};
const ROAD_TEXTURES: PbrTextureUrls = {
  diffuse: '/textures/road/diffuse.jpg',
  normal: '/textures/road/normal.jpg',
  rough: '/textures/road/rough.jpg',
};
const SIDEWALK_TEXTURES: PbrTextureUrls = {
  diffuse: '/textures/sidewalk/diffuse.jpg',
  normal: '/textures/sidewalk/normal.jpg',
  ao: '/textures/sidewalk/ao.jpg',
  rough: '/textures/sidewalk/rough.jpg',
};
const HUB_TEXTURES: PbrTextureUrls = {
  diffuse: '/textures/hub/diffuse.jpg',
  normal: '/textures/hub/normal.jpg',
  rough: '/textures/hub/rough.jpg',
};
const GRASS_METERS_PER_TILE = 4;
const CONCRETE_METERS_PER_TILE = 6;
const SIDEWALK_METERS_PER_TILE = 2.4;

function houseFootprint(tier: number): { x: number; y: number; z: number } {
  return { x: 8 + tier * 0.6, y: 3.4 + tier * 1.1, z: 8 + tier * 0.6 };
}

export function buildWorld(
  app: pc.Application,
  map: WorldMap,
  myProfile: XpProfile,
  neighbors: Neighbor[],
): WorldScene {
  const root = new pc.Entity('world');
  app.root.addChild(root);

  const obstacles: Obstacle[] = [];
  const interactables: InteractTarget[] = [];

  // --- Ground (flat color instantly; a real CC0 grass PBR texture streams in over it) ---
  const half = map.worldHalf;
  const groundSize = half * 2;
  const ground = box('ground', { x: groundSize, y: 1, z: groundSize }, { x: 0, y: -0.5, z: 0 }, colorMaterial(GROUND_COLOR));
  root.addChild(ground);
  void loadPbrTextures(app, GRASS_TEXTURES).then((tex) => {
    // The leafy-grass photo set reads dry-yellow under the warm HDRI sun; a green
    // multiply brings it back toward the lush grass of the splat hero's own lawn.
    if (ground.render) ground.render.material = pbrMaterial(tex, GRASS_METERS_PER_TILE, groundSize, '#86b06a');
  });

  // --- Roads (real photographed asphalt with lane markings + sidewalks) ---
  buildRoads(app, root, map);

  // --- District plazas (flat color instantly; a real CC0 concrete PBR texture streams in over it) ---
  // Outer districts get a modest square around their buildings instead of a
  // desert-sized slab — grass belongs between the plazas and the roads.
  for (const district of map.districts) {
    const plazaSize = district.radius * (district.id === 'center' ? 0.9 : 0.55);
    const plaza = box(
      `plaza-${district.id}`,
      { x: plazaSize, y: 0.1, z: plazaSize },
      { x: district.center.x, y: 0.05, z: district.center.z },
      colorMaterial(SIDEWALK_COLOR),
    );
    root.addChild(plaza);
    void loadPbrTextures(app, CONCRETE_TEXTURES).then((tex) => {
      // Cool grey multiply — the raw pavement photo reads dusty-brown under the warm sun.
      if (plaza.render) plaza.render.material = pbrMaterial(tex, CONCRETE_METERS_PER_TILE, plazaSize, '#b6bdc6');
    });

    // A pair of photoscanned café table sets on the plaza's approach side.
    const toCenter = Math.atan2(-district.center.x, -district.center.z);
    const edge = plazaSize / 2 - 3;
    for (const side of [-1, 1]) {
      const bx = district.center.x + Math.sin(toCenter) * edge + Math.cos(toCenter) * side * 6;
      const bz = district.center.z + Math.cos(toCenter) * edge - Math.sin(toCenter) * side * 6;
      void spawnProp(app, '/props/seating/seating.gltf', 0.75).then((seating) => {
        if (!seating) return;
        seating.setPosition(bx, 0.1, bz);
        seating.setEulerAngles(0, side * 60, 0);
        root.addChild(seating);
      });
    }
  }

  // --- Buildings ---
  for (const building of map.buildings) {
    // Urban rule: entrance faces the plaza heart when the building stands on a
    // plaza, otherwise it faces (and the plot gate opens toward) the nearest road.
    const district = map.districts.find((d) => d.id === building.districtId);
    const inPlaza = onPlaza(map, building.position.x, building.position.z);
    const facing =
      building.type === 'npc'
        ? 0
        : inPlaza && district
          ? snap90(
              (Math.atan2(district.center.x - building.position.x, district.center.z - building.position.z) * 180) /
                Math.PI,
            )
          : facingToNearestRoad(map, building.position.x, building.position.z);

    if (!building.locked && building.splat) {
      // Placeholder box renders instantly; the licensed splat scan streams in behind it (DR-10).
      const placeholder = box(
        `bldg-${building.id}`,
        { x: building.size.x, y: building.size.y, z: building.size.z },
        { x: building.position.x, y: building.size.y / 2, z: building.position.z },
        colorMaterial(building.color),
      );
      root.addChild(placeholder);

      void spawnSplatBuilding(app, building.splat).then((splat) => {
        if (!splat) return;
        splat.setPosition(building.position.x, 0, building.position.z);
        root.addChild(splat);
        placeholder.destroy();
      });
    } else if (building.type === 'npc') {
      // EVA: a real standing character on a glowing beacon ring (box placeholder
      // shows until the shared rigged character streams in).
      const glow = box(
        `npc-glow-${building.id}`,
        { x: 3, y: 0.08, z: 3 },
        { x: building.position.x, y: 0.04, z: building.position.z },
        colorMaterial(building.color, true),
      );
      root.addChild(glow);
      const placeholder = box(
        `bldg-${building.id}`,
        { x: building.size.x, y: building.size.y, z: building.size.z },
        { x: building.position.x, y: building.size.y / 2, z: building.position.z },
        colorMaterial(building.color, true),
      );
      root.addChild(placeholder);
      void spawnCharacter(app).then((inst) => {
        if (!inst) return;
        inst.anim.speed = 0; // frozen on the clip's idle pose
        inst.entity.setPosition(building.position.x, 0.1, building.position.z);
        inst.entity.setEulerAngles(0, 180, 0); // face the plaza/spawn side
        root.addChild(inst.entity);
        placeholder.destroy();
      });
    } else {
      // Photo-facade massing box: real photographed windows at real-world scale.
      // Locked buildings get a bare darkened concrete shell instead (Blueprint §3.2).
      const pool =
        !building.locked && (building.type === 'school' || building.type === 'career' || building.type === 'company')
          ? TYPE_POOLS[building.type]
          : [FACADE_SETS.wall];
      const entity = spawnFacadeBuilding(app, {
        id: building.id,
        size: building.size,
        pool,
        tint: building.locked ? LOCKED_TINT : undefined,
      });
      entity.setPosition(building.position.x, 0, building.position.z);
      entity.setEulerAngles(0, facing, 0);
      root.addChild(entity);
    }

    // Collision box follows the rotated footprint (splats keep their own data
    // rotation, so their obstacle uses the raw size).
    const swap = !building.splat && building.type !== 'npc' && facingSwapsFootprint(facing);
    const ox = (swap ? building.size.z : building.size.x) / 2 + 0.5;
    const oz = (swap ? building.size.x : building.size.z) / 2 + 0.5;
    obstacles.push({
      minX: building.position.x - ox,
      maxX: building.position.x + ox,
      minZ: building.position.z - oz,
      maxZ: building.position.z + oz,
    });

    // Freestanding buildings (off the plazas) get a walled garden plot whose
    // gate sits on the same axis as the entrance — street → gate → path → door.
    if (building.type !== 'npc' && !inPlaza) {
      addPlot(
        app,
        root,
        obstacles,
        building.position.x,
        building.position.z,
        Math.max(building.size.x, building.size.z) / 2,
        building.splat ? 12 : 4.5, // splat scans carry their own lawn — fence beyond it
        facing,
        true,
      );
    }

    if (!building.locked && (building.interaction || building.type === 'npc')) {
      interactables.push({
        building,
        position: new pc.Vec3(building.position.x, 0, building.position.z),
        radius: Math.max(building.size.x, building.size.z) / 2 + 4,
      });
    }
  }

  // --- Houses (data-driven from profiles: DR-05) ---
  const houseEntities = new Map<string, pc.Entity>();
  const plottedSlots = new Set<string>();

  const buildHouse = (slot: HouseSlot, level: number, _anonymous: boolean) => {
    houseEntities.get(slot.slotId)?.destroy();

    const tier = Math.min(Math.max(level, 1), 5);
    const size = houseFootprint(tier);
    const house = spawnFacadeBuilding(app, {
      id: `${slot.slotId}-t${tier}`, // tier in the hash key: leveling up can re-roll the facade
      size,
      pool: TYPE_POOLS.house,
    });
    const inPlaza = onPlaza(map, slot.position.x, slot.position.z);
    const district = map.districts.find((d) => d.id === slot.districtId);
    const facing =
      inPlaza && district
        ? snap90(
            (Math.atan2(district.center.x - slot.position.x, district.center.z - slot.position.z) * 180) / Math.PI,
          )
        : facingToNearestRoad(map, slot.position.x, slot.position.z);
    house.setPosition(slot.position.x, 0, slot.position.z);
    house.setEulerAngles(0, facing, 0);
    root.addChild(house);
    houseEntities.set(slot.slotId, house);

    if (!inPlaza && !plottedSlots.has(slot.slotId)) {
      plottedSlots.add(slot.slotId); // rebuilds swap the house, not the plot
      addPlot(app, root, obstacles, slot.position.x, slot.position.z, Math.max(size.x, size.z) / 2, 4, facing, true);
    }
  };

  const neighborByUser = new Map(neighbors.map((n) => [n.userId, n]));
  for (const slot of map.houseSlots) {
    if (slot.ownerUserId === myProfile.userId) {
      buildHouse(slot, myProfile.level, false);
    } else {
      const neighbor = neighborByUser.get(slot.ownerUserId);
      buildHouse(slot, neighbor?.level ?? 1, neighbor?.anonymous ?? true);
    }
    obstacles.push({
      minX: slot.position.x - 5,
      maxX: slot.position.x + 5,
      minZ: slot.position.z - 5,
      maxZ: slot.position.z + 5,
    });
  }

  // --- Background fabric buildings (ambience: photo facades, non-interactive) ---
  scatterFabricBuildings(app, root, map, obstacles);

  const mySlot = map.houseSlots.find((s) => s.ownerUserId === myProfile.userId);

  return {
    obstacles,
    interactables,
    rebuildMyHouse(profile: XpProfile) {
      if (mySlot) {
        buildHouse(mySlot, profile.level, false);
      }
    },
    districtAt(x: number, z: number) {
      for (const district of map.districts) {
        const dx = x - district.center.x;
        const dz = z - district.center.z;
        if (Math.hypot(dx, dz) <= district.radius) {
          return district;
        }
      }
      return null;
    },
  };
}

const WALL_TEXTURES: PbrTextureUrls = {
  diffuse: '/textures/wall/diffuse.jpg',
  normal: '/textures/wall/normal.jpg',
  ao: '/textures/wall/ao.jpg',
  rough: '/textures/wall/rough.jpg',
};

const PLOT_WALL_H = 1.25;
const PLOT_WALL_T = 0.3;
const PLOT_GATE_W = 4;

/**
 * Walled plot around a freestanding building — a low photo-plaster boundary
 * wall with a gate opening facing the city center and (optionally) a paved
 * path from the gate to the entrance. Buildings standing on district plazas
 * don't get one (urban frontage); everything out on the grass does, so nothing
 * reads as "a box dropped on a lawn".
 */
function addPlot(
  app: pc.Application,
  root: pc.Entity,
  obstacles: Obstacle[],
  cx: number,
  cz: number,
  buildingHalf: number,
  margin: number,
  gateYaw: number,
  withPath: boolean,
): void {
  const half = buildingHalf + margin;
  const wallTex = loadPbrTextures(app, WALL_TEXTURES);
  // The gate opens on the same side as the building entrance (gateYaw = the
  // building's facing), completing the street → gate → path → door axis.
  const norm = snap90(gateYaw);
  const gateAxis = norm % 180 === 90 ? 'x' : 'z';
  const gateDir = norm === 0 || norm === 90 ? 1 : -1;

  // Yard surface — visibly different ground inside the wall than the public
  // grass outside (paved court, warm sand tint).
  const yard = box(
    'plot-yard',
    { x: half * 2 - 0.2, y: 0.06, z: half * 2 - 0.2 },
    { x: cx, y: 0.03, z: cz },
    colorMaterial('#b9b1a0'),
  );
  root.addChild(yard);
  const yardSize = Math.round(half); // quantized → shared-ish tiling
  void loadPbrTextures(app, SIDEWALK_TEXTURES).then((tex) => {
    if (yard.render) yard.render.material = pbrMaterial(tex, SIDEWALK_METERS_PER_TILE, yardSize * 2, '#c8bfa9');
  });

  const spawnWall = (wx: number, wz: number, sx: number, sz: number) => {
    const wall = box(
      'plot-wall',
      { x: sx, y: PLOT_WALL_H, z: sz },
      { x: wx, y: PLOT_WALL_H / 2, z: wz },
      colorMaterial('#cfccc4'),
    );
    root.addChild(wall);
    const len = Math.max(sx, sz);
    const lenQ = Math.max(2, Math.round(len / 2) * 2); // quantized → shared materials
    void wallTex.then((tex) => {
      if (wall.render) wall.render.material = pbrMaterialUV(tex, lenQ / 5, PLOT_WALL_H / 5);
    });
    obstacles.push({
      minX: wx - sx / 2,
      maxX: wx + sx / 2,
      minZ: wz - sz / 2,
      maxZ: wz + sz / 2,
      maxY: PLOT_WALL_H,
    });
  };

  for (const axis of ['x', 'z'] as const) {
    for (const dir of [-1, 1]) {
      const isGateWall = axis === gateAxis && dir === gateDir;
      if (axis === 'z') {
        // walls at z = cz ± half, running along x
        const wz = cz + dir * half;
        if (isGateWall) {
          const seg = (half * 2 - PLOT_GATE_W) / 2;
          spawnWall(cx - (PLOT_GATE_W / 2 + seg / 2), wz, seg, PLOT_WALL_T);
          spawnWall(cx + (PLOT_GATE_W / 2 + seg / 2), wz, seg, PLOT_WALL_T);
        } else {
          spawnWall(cx, wz, half * 2, PLOT_WALL_T);
        }
      } else {
        const wx = cx + dir * half;
        if (isGateWall) {
          const seg = (half * 2 - PLOT_GATE_W) / 2;
          spawnWall(wx, cz - (PLOT_GATE_W / 2 + seg / 2), PLOT_WALL_T, seg);
          spawnWall(wx, cz + (PLOT_GATE_W / 2 + seg / 2), PLOT_WALL_T, seg);
        } else {
          spawnWall(wx, cz, PLOT_WALL_T, half * 2);
        }
      }
    }
  }

  if (withPath) {
    // Paved path from the gate straight to the entrance door.
    const pathLen = margin + 0.4;
    const px = gateAxis === 'x' ? cx + gateDir * (buildingHalf + pathLen / 2 - 0.2) : cx;
    const pz = gateAxis === 'z' ? cz + gateDir * (buildingHalf + pathLen / 2 - 0.2) : cz;
    const path = box(
      'plot-path',
      { x: gateAxis === 'x' ? pathLen : PLOT_GATE_W - 1, y: 0.1, z: gateAxis === 'z' ? pathLen : PLOT_GATE_W - 1 },
      { x: px, y: 0.05, z: pz },
      colorMaterial(SIDEWALK_COLOR),
    );
    root.addChild(path);
    void loadPbrTextures(app, CONCRETE_TEXTURES).then((tex) => {
      if (path.render) path.render.material = pbrMaterial(tex, CONCRETE_METERS_PER_TILE, pathLen, '#b6bdc6');
    });
  }
}

/** True when the point sits on one of the district plaza squares. */
function onPlaza(map: WorldMap, x: number, z: number, extra = 0): boolean {
  for (const d of map.districts) {
    const half = (d.radius * (d.id === 'center' ? 0.9 : 0.55)) / 2 + extra;
    if (Math.abs(x - d.center.x) < half && Math.abs(z - d.center.z) < half) return true;
  }
  return false;
}

/** Snaps an arbitrary yaw to the street grid (multiples of 90°). */
function snap90(yawDeg: number): number {
  return ((Math.round(yawDeg / 90) * 90) % 360 + 360) % 360;
}

/** Yaw (snapped to the grid) that turns a building's entrance face (+Z) toward
 * the nearest road — the base rule of the city's urban layout: every building
 * addresses a street. */
function facingToNearestRoad(map: WorldMap, x: number, z: number): number {
  let best = Infinity;
  let bx = 0;
  let bz = 0;
  for (const r of map.roads) {
    const abx = r.to.x - r.from.x;
    const abz = r.to.z - r.from.z;
    const t = pc.math.clamp(((x - r.from.x) * abx + (z - r.from.z) * abz) / (abx * abx + abz * abz), 0, 1);
    const nx = r.from.x + abx * t;
    const nz = r.from.z + abz * t;
    const d = Math.hypot(x - nx, z - nz);
    if (d < best) {
      best = d;
      bx = nx;
      bz = nz;
    }
  }
  return snap90((Math.atan2(bx - x, bz - z) * 180) / Math.PI);
}

/** Rotating a rectangular footprint by ±90° swaps its X/Z extents. */
function facingSwapsFootprint(yawDeg: number): boolean {
  return snap90(yawDeg) % 180 === 90;
}

/** Deterministic [0,1) hash — fabric placement must be stable across sessions. */
function hash01(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  }
  return (h >>> 0) / 4294967296;
}

/** Height/footprint character of each district's background fabric. */
const FABRIC_PROFILE: Record<string, { hMin: number; hMax: number; fMin: number; fMax: number; pool: keyof typeof TYPE_POOLS }> = {
  education: { hMin: 9, hMax: 18, fMin: 14, fMax: 20, pool: 'school' },
  career: { hMin: 12, hMax: 22, fMin: 14, fMax: 20, pool: 'career' },
  business: { hMin: 16, hMax: 34, fMin: 14, fMax: 22, pool: 'company' },
  residential: { hMin: 8, hMax: 14, fMin: 10, fMax: 15, pool: 'house' },
  center: { hMin: 10, hMax: 16, fMin: 12, fMax: 18, pool: 'career' },
};

/**
 * Non-interactive background buildings that give the city urban density:
 * a ring around each district plus a few flanking each road. Purely visual
 * ambience (like the benches) — semantic buildings stay data-driven (DR-05).
 * Placement is hash-deterministic and rejects spots near semantic buildings,
 * houses, plazas, roads and each other. All get collision obstacles.
 */
function scatterFabricBuildings(
  app: pc.Application,
  root: pc.Entity,
  map: WorldMap,
  obstacles: Obstacle[],
): void {
  const placed: Array<{ x: number; z: number; half: number }> = [];
  const keepOut: Array<{ x: number; z: number; r: number }> = [];
  for (const b of map.buildings) {
    keepOut.push({ x: b.position.x, z: b.position.z, r: Math.max(b.size.x, b.size.z) / 2 + 10 });
  }
  for (const s of map.houseSlots) {
    keepOut.push({ x: s.position.x, z: s.position.z, r: 14 });
  }

  const distToRoad = (x: number, z: number): number => {
    let min = Infinity;
    for (const road of map.roads) {
      const ax = road.from.x, az = road.from.z;
      const bx = road.to.x, bz = road.to.z;
      const abx = bx - ax, abz = bz - az;
      const t = pc.math.clamp(((x - ax) * abx + (z - az) * abz) / (abx * abx + abz * abz), 0, 1);
      min = Math.min(min, Math.hypot(x - (ax + abx * t), z - (az + abz * t)));
    }
    return min;
  };

  const tryPlace = (id: string, x: number, z: number, footprint: number, height: number, pool: keyof typeof TYPE_POOLS): void => {
    const half = footprint / 2;
    if (Math.abs(x) > map.worldHalf - 20 || Math.abs(z) > map.worldHalf - 20) return;
    for (const k of keepOut) {
      if (Math.hypot(x - k.x, z - k.z) < k.r + half) return;
    }
    for (const p of placed) {
      if (Math.hypot(x - p.x, z - p.z) < p.half + half + 7) return;
    }
    for (const d of map.districts) {
      const plazaHalf = (d.radius * (d.id === 'center' ? 0.9 : 0.55)) / 2;
      if (Math.abs(x - d.center.x) < plazaHalf + half + 3 && Math.abs(z - d.center.z) < plazaHalf + half + 3) return;
    }
    if (distToRoad(x, z) < 5 + SIDEWALK_WIDTH + half + 6) return; // + plot-wall clearance

    // Quantize so equal-sized walls share cached facade materials.
    const size = { x: Math.round(footprint), y: Math.round(height), z: Math.round(footprint * (0.8 + hash01(id + 'd') * 0.4)) };
    const entity = spawnFacadeBuilding(app, { id, size, pool: TYPE_POOLS[pool] });
    const facing = facingToNearestRoad(map, x, z);
    entity.setPosition(x, 0, z);
    entity.setEulerAngles(0, facing, 0);
    root.addChild(entity);
    placed.push({ x, z, half: Math.max(size.x, size.z) / 2 });
    const swap = facingSwapsFootprint(facing);
    const ox = (swap ? size.z : size.x) / 2 + 0.5;
    const oz = (swap ? size.x : size.z) / 2 + 0.5;
    obstacles.push({ minX: x - ox, maxX: x + ox, minZ: z - oz, maxZ: z + oz });
    addPlot(app, root, obstacles, x, z, Math.max(size.x, size.z) / 2, 3.5, facing, false);
  };

  // Ring of fabric around each district.
  for (const district of map.districts) {
    const profile = FABRIC_PROFILE[district.id] ?? FABRIC_PROFILE.center;
    const count = district.id === 'center' ? 4 : 7;
    for (let i = 0; i < count; i++) {
      const id = `fab-${district.id}-${i}`;
      const angle = (i / count) * Math.PI * 2 + hash01(id + 'a') * 0.9;
      const radius = district.radius * (0.6 + hash01(id + 'g') * 0.32);
      tryPlace(
        id,
        district.center.x + Math.sin(angle) * radius,
        district.center.z + Math.cos(angle) * radius,
        profile.fMin + hash01(id + 'f') * (profile.fMax - profile.fMin),
        profile.hMin + hash01(id + 'h') * (profile.hMax - profile.hMin),
        profile.pool,
      );
    }
  }

  // Street-front fabric flanking each road, so walking never crosses empty steppe.
  map.roads.forEach((road, r) => {
    const dx = road.to.x - road.from.x;
    const dz = road.to.z - road.from.z;
    const len = Math.hypot(dx, dz);
    const px = -dz / len, pz = dx / len; // unit perpendicular
    for (let i = 0; i < 4; i++) {
      const id = `fab-road-${r}-${i}`;
      const t = 0.28 + (i / 4) * 0.5 + hash01(id + 't') * 0.08;
      const side = i % 2 === 0 ? 1 : -1;
      const offset = road.width / 2 + 12 + hash01(id + 'o') * 10;
      tryPlace(
        id,
        road.from.x + dx * t + px * side * offset,
        road.from.z + dz * t + pz * side * offset,
        11 + hash01(id + 'f') * 8,
        9 + hash01(id + 'h') * 12,
        (['house', 'career', 'school'] as const)[Math.floor(hash01(id + 'p') * 3)],
      );
    }
  });
}

const SIDEWALK_WIDTH = 1.8;

/**
 * One photographed-asphalt strip per road segment (lane markings run along the
 * texture's V axis = the strip's length), flanked by raised sidewalk strips,
 * with a plain-asphalt square where segments meet. Tiny per-segment height
 * offsets keep overlapping strips from z-fighting near the hub.
 */
function buildRoads(app: pc.Application, root: pc.Entity, map: WorldMap): void {
  const roads = map.roads;
  const roadTex = loadPbrTextures(app, ROAD_TEXTURES);
  const walkTex = loadPbrTextures(app, SIDEWALK_TEXTURES);

  // Sidewalks/lamps stay clear of whatever each segment end meets: a district
  // plaza (stop at its edge) or another road (stop short of the junction).
  const trimFor = (p: { x: number; z: number }): number => {
    for (const d of map.districts) {
      if (Math.hypot(p.x - d.center.x, p.z - d.center.z) < 2) {
        return (d.radius * (d.id === 'center' ? 0.9 : 0.55)) / 2 + 4;
      }
    }
    return 16;
  };

  roads.forEach((road, idx) => {
    const dx = road.to.x - road.from.x;
    const dz = road.to.z - road.from.z;
    const length = Math.hypot(dx, dz);
    const angle = (Math.atan2(dx, dz) * 180) / Math.PI;
    const lift = idx * 0.004;

    const holder = new pc.Entity(`road-${idx}`);
    holder.setPosition((road.from.x + road.to.x) / 2, 0, (road.from.z + road.to.z) / 2);
    holder.setEulerAngles(0, angle, 0);
    root.addChild(holder);

    const strip = box('asphalt', { x: road.width, y: 0.1, z: length }, { x: 0, y: 0.05 + lift, z: 0 }, colorMaterial(ROAD_COLOR));
    holder.addChild(strip);
    void roadTex.then((tex) => {
      if (strip.render) strip.render.material = pbrMaterialUV(tex, 1, length / road.width);
    });

    // Local -Z points toward road.from, +Z toward road.to.
    const trimStart = trimFor(road.from);
    const trimEnd = trimFor(road.to);
    const walkLen = length - trimStart - trimEnd;
    if (walkLen > 8) {
      const walkCenter = (trimStart - trimEnd) / 2;
      for (const side of [-1, 1]) {
        const walk = box(
          'sidewalk',
          { x: SIDEWALK_WIDTH, y: 0.18, z: walkLen },
          { x: side * (road.width / 2 + SIDEWALK_WIDTH / 2), y: 0.09 + lift, z: walkCenter },
          colorMaterial(SIDEWALK_COLOR),
        );
        holder.addChild(walk);
        void walkTex.then((tex) => {
          if (walk.render) {
            walk.render.material = pbrMaterialUV(tex, SIDEWALK_WIDTH / SIDEWALK_METERS_PER_TILE, walkLen / SIDEWALK_METERS_PER_TILE);
          }
        });
      }
    }

    // Photoscanned street lamps every ~50m, alternating sides, just past the sidewalk.
    const lampCount = Math.floor(length / 50);
    const tLampMin = trimFor(road.from) / length;
    const tLampMax = 1 - trimFor(road.to) / length;
    for (let li = 0; li < lampCount; li++) {
      const t = tLampMin + ((li + 0.5) / lampCount) * (tLampMax - tLampMin);
      const side = li % 2 === 0 ? 1 : -1;
      void spawnProp(app, '/props/lamp/lamp.gltf', 4.2).then((lamp) => {
        if (!lamp) return;
        lamp.setPosition(0, 0.05, 0);
        const lampHolder = new pc.Entity('lamp');
        lampHolder.addChild(lamp);
        lampHolder.setPosition(
          road.from.x + dx * t + (-dz / length) * side * (road.width / 2 + SIDEWALK_WIDTH + 0.7),
          0,
          road.from.z + dz * t + (dx / length) * side * (road.width / 2 + SIDEWALK_WIDTH + 0.7),
        );
        // Face the roadway so the arm reaches over it.
        lampHolder.setEulerAngles(0, angle + (side > 0 ? 90 : -90), 0);
        root.addChild(lampHolder);
      });
    }
  });

  // Plain-asphalt square where the marked strips meet, covering their overlap.
  const hubs = new Map<string, { x: number; z: number; width: number; count: number }>();
  for (const road of roads) {
    for (const point of [road.from, road.to]) {
      const key = `${Math.round(point.x)}:${Math.round(point.z)}`;
      const hub = hubs.get(key) ?? { x: point.x, z: point.z, width: road.width, count: 0 };
      hub.count += 1;
      hub.width = Math.max(hub.width, road.width);
      hubs.set(key, hub);
    }
  }
  const hubTex = loadPbrTextures(app, HUB_TEXTURES);
  for (const hub of hubs.values()) {
    if (hub.count < 2) continue;
    const size = hub.width * 1.8;
    const square = box('hub', { x: size, y: 0.16, z: size }, { x: hub.x, y: 0.06, z: hub.z }, colorMaterial(ROAD_COLOR));
    root.addChild(square);
    void hubTex.then((tex) => {
      // Asphalt015 photographs much lighter than Road007 — darken to match, or the
      // junction reads as a white slab from the helicopter view.
      if (square.render) square.render.material = pbrMaterialUV(tex, size / 6, size / 6, '#77797c');
    });
  }
}

